/**
 * API utilities
 * 
 * This module provides functions to interact with the backend API,
 * including lead submission with encryption.
 */

import axios from 'axios';
import { Encrypt, Decrypt } from './encryption';

/**
 * Lead type enum
 */
export enum LeadType {
  message = 'message'
}

/**
 * Store configuration interface
 */
interface Store {
  _id: string;
  api?: {
    endpoint: string;
    leads: string;
  };
}

/**
 * Lead data interface
 */
interface LeadData {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  vehicle?: string; // Format: "Brand Model Version Year/ModelYear"
  id?: string;
  plaque?: string;
  brand?: string;
  model?: string;
  version?: string;
  year?: string; // Format: "Year/ModelYear"
  price?: string; // Format: "R$ 450.000,00"
  mileage?: string;
  /** Origem do lead (ex.: site white-label) */
  channel?: string;
  [key: string]: any;
}

/**
 * Send a lead to the API with encryption
 * 
 * @param type - Type of lead (message, etc.)
 * @param store - Store configuration object
 * @param data - Lead data to send
 * @returns Decrypted response from the API
 * @throws Error if API call fails or encryption/decryption fails
 * 
 * @example
 * ```typescript
 * const response = await LEAD(LeadType.message, store, {
 *   name: 'João Silva',
 *   email: 'joao@email.com',
 *   phone: '(11) 99999-9999',
 *   message: 'Tenho interesse no veículo...',
 *   vehicle: `${ad?.brand} ${ad?.model} ${ad?.type == 1 ? `${ad?.version} ` : ' '}${ad?.manufactureYear}/${ad?.modelYear}`,
 *   id: '123',
 *   plaque: 'ABC-1234',
 *   brand: 'BMW',
 *   model: 'X5',
 *   version: 'M50i',
 *   year: `${ad?.manufactureYear}/${ad?.modelYear}`,
 *   price: 'R$ 45.000,00',
 *   mileage: 1200
 * });
 * ```
 */
export const LEAD = async (
  type: LeadType | string,
  store: Store,
  data: LeadData
): Promise<any> => {
  try {
    // Validate required parameters
    if (!store?._id) {
      throw new Error('Store ID is required');
    }

    if (!store?.api?.endpoint || !store?.api?.leads) {
      throw new Error('Store API configuration is missing');
    }

    // Prepare query object (channel inside encrypted payload, not query string)
    const query = {
      type,
      storeId: store._id,
      data: {
        ...data,
        channel: 'Site'
      }
    };

    // Encrypt the query
    const encryptedQuery = await Encrypt(query);

    const apiUrl = `${store.api.endpoint}/${store.api.leads}`;

    // Send encrypted data to API
    const response = await axios.post(apiUrl, {
      data: encryptedQuery
    }, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000 // 30 seconds timeout
    });

    // Decrypt the response
    const decryptedResponse = await Decrypt(response.data);

    return decryptedResponse;
  } catch (error) {
    console.error('Error sending lead:', error);

    // Handle axios errors
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with error status
        throw new Error(
          `API error: ${error.response.status} - ${error.response.statusText}`
        );
      } else if (error.request) {
        // Request was made but no response received
        throw new Error('No response from server. Please check your connection.');
      } else {
        // Error in request setup
        throw new Error(`Request error: ${error.message}`);
      }
    }

    // Re-throw other errors
    throw error;
  }
};

/**
 * Ensure URL uses absolute HTTPS protocol
 * Converts relative URLs or HTTP to HTTPS
 * 
 * @param url - URL to convert
 * @returns Absolute HTTPS URL
 * 
 * @example
 * ```typescript
 * ensureAbsoluteHttps('api.example.com/endpoint') // 'https://api.example.com/endpoint'
 * ensureAbsoluteHttps('http://api.example.com') // 'https://api.example.com'
 * ensureAbsoluteHttps('https://api.example.com') // 'https://api.example.com'
 * ```
 */
export const ensureAbsoluteHttps = (url: string): string => {
  if (!url) return url;
  
  // Remove any leading/trailing whitespace
  url = url.trim();
  
  // If URL already starts with https://, return as is
  if (url.startsWith('https://')) {
    return url;
  }
  
  // If URL starts with http://, replace with https://
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  
  // If URL doesn't have protocol, add https://
  if (!url.startsWith('//')) {
    return `https://${url}`;
  }
  
  // If URL starts with //, add https:
  return `https:${url}`;
};

/**
 * Helper function to format phone number for API
 * Removes all non-numeric characters
 */
export const formatPhoneForAPI = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Helper function to prepare vehicle lead data
 */
export const prepareVehicleLead = (formData: {
  name: string;
  email: string;
  phone: string;
  message: string;
}, vehicle: any): LeadData => {
  return {
    name: formData.name,
    email: formData.email,
    phone: formatPhoneForAPI(formData.phone),
    message: formData.message,
    id: vehicle.id,
    brand: vehicle.brand,
    plaque: vehicle.plaque,
    model: vehicle.model,
    version: vehicle.version,
    year: vehicle.modelYear,
    price: vehicle.price,
    mileage: vehicle.mileage,
    vehicle: vehicle.vehicle
  };
};

/**
 * FIPE API utilities
 * Get vehicle price from FIPE table
 */
export const API_FIPE = {
  /**
   * Get FIPE price for a vehicle
   * 
   * @param store - Store configuration object
   * @param type - Vehicle type (car, motorcycle, truck)
   * @param brand - Vehicle brand
   * @param model - Vehicle model
   * @param modelYear - Vehicle model year
   * @returns Decrypted FIPE data from the API
   * @throws Error if API call fails or encryption/decryption fails
   * 
   * @example
   * ```typescript
   * const fipeData = await API_FIPE.get(store, 'car', 'BMW', 'X5', '2024');
   * ```
   */
  get: async (
    store: Store,
    type: string,
    brand: string,
    model: string,
    modelYear: string | number
  ): Promise<any> => {
    try {
      // Validate required parameters
      if (!store?.api?.endpoint) {
        throw new Error('Store API endpoint is required');
      }

      // Prepare query object
      const query = {
        type,
        brand,
        model,
        modelYear
      };

      // Encrypt the query
      const encryptedQuery = await Encrypt(query);

      // Build API URL
      const apiUrl = ensureAbsoluteHttps(`${store.api.endpoint}/app/fipe`);

      // Send encrypted data to API via GET with query params
      const response = await axios.get(apiUrl, {
        params: {
          data: encryptedQuery
        },
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 seconds timeout
      });

      // Decrypt the response
      const decryptedResponse = await Decrypt(response.data);

      return decryptedResponse;
    } catch (error) {
      console.error('Error fetching FIPE data:', error);

      // Handle axios errors
      if (axios.isAxiosError(error)) {
        if (error.response) {
          throw new Error(
            `FIPE API error: ${error.response.status} - ${error.response.statusText}`
          );
        } else if (error.request) {
          throw new Error('No response from FIPE API. Please check your connection.');
        } else {
          throw new Error(`FIPE request error: ${error.message}`);
        }
      }

      // Re-throw other errors
      throw error;
    }
  }
};

